import { QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router';
import { ToastProvider } from '@efira/ui';

import { queryClient } from './api/queries';

import { RedirectIfAuthed, RequireAuth, SessionProvider } from './auth/session';
import { AccountZone } from './dashboard/AccountZone';
import { ConsoleShell } from './console/ConsoleShell';
import { LegacyZoneLayout } from './dashboard/LegacyZoneLayout';
import {
  AffiliateModal,
  AnalyticsModal,
  BillingCancelledModal,
  BillingDoneModal,
  FeedbackRoute,
  GamesModal,
  HistoryModal,
  PlanModal,
  SettingsModal,
} from './console/modals/routes';
import { ApiErrorToasts } from './api/errors';
import { ReferralScreen } from './screens/ReferralScreen';
import { ChannelsZone } from './dashboard/ChannelsZone';
import { EmptyZone } from './dashboard/EmptyZone';
import { OverlayZone } from './dashboard/OverlayZone';
import { StreamZone } from './dashboard/StreamZone';
import { ZONES } from './dashboard/zones';
import { CheckEmailScreen } from './screens/CheckEmailScreen';
import { ForgotPasswordScreen } from './screens/ForgotPasswordScreen';
import { ResetPasswordScreen } from './screens/ResetPasswordScreen';
import { SignInScreen } from './screens/SignInScreen';
import { SignUpScreen } from './screens/SignUpScreen';
import { VerifyEmailScreen } from './screens/VerifyEmailScreen';
import { ShowcaseScreen } from './dev/ShowcaseScreen';

/**
 * Маршруты приложения.
 *
 * Экраны auth лежат в корне, продуктовая часть - под /app и целиком за
 * RequireAuth. Такое деление означает, что незащищённый маршрут внутри
 * dashboard физически нельзя забыть обернуть.
 *
 * ВКЛАДКИ ЗОН СНЯТЫ 2026-09-18. Модель была неверной: стример в эфире не
 * переключает вкладки, он смотрит на один экран, где видно всё сразу. На месте
 * вкладок - консоль в три колонки; окна настроек, аналитики, истории,
 * партнёрской программы, смены игры и тарифа - ДОЧЕРНИЕ маршруты консоли, и
 * именно поэтому консоль под открытым окном не перезагружается.
 *
 * Экраны прежних зон НЕ УДАЛЕНЫ: их содержимое переезжает в зоны консоли на
 * этапе 2. До переезда они живут под /app/legacy без вкладок - чтобы было чем
 * сверяться и чтобы сквозные сценарии продолжали проверять то, что уже
 * работает. Что куда переезжает - docs/PHASE4_STATE.md.
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <ToastProvider>
          {/* Скрипт FlyonUI: загрузка и переинициализация при смене маршрута. */}
          {/* Ни одна мутация не завершается тихо: ошибки всплывают тостом. */}
          <ApiErrorToasts />
          <Routes>
            <Route path="/" element={<Navigate to="/app" replace />} />

            <Route
              path="/signup"
              element={
                <RedirectIfAuthed>
                  <SignUpScreen />
                </RedirectIfAuthed>
              }
            />
            <Route
              path="/login"
              element={
                <RedirectIfAuthed>
                  <SignInScreen />
                </RedirectIfAuthed>
              }
            />
            <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
            <Route path="/reset-password" element={<ResetPasswordScreen />} />
            <Route path="/check-email" element={<CheckEmailScreen />} />
            <Route path="/verify-email" element={<VerifyEmailScreen />} />
            {/* Витрина компонентов: публична, без сессии, проверяется аудитом a11y. */}
            <Route path="/showcase" element={<ShowcaseScreen />} />
            {/* Переход по партнёрской ссылке: засчитать и увести в
                регистрацию. Экран без содержимого - по ссылке приходят за
                продуктом, а не за рассказом о программе. */}
            <Route path="/r/:code" element={<ReferralScreen />} />

            {/* Консоль. Окна модального слоя - её дочерние маршруты. */}
            <Route
              path="/app"
              element={
                <RequireAuth>
                  <ConsoleShell />
                </RequireAuth>
              }
            >
              <Route path="settings" element={<SettingsModal />} />
              <Route path="settings/:section" element={<SettingsModal />} />
              <Route path="analytics" element={<AnalyticsModal />} />
              <Route path="analytics/:tab" element={<AnalyticsModal />} />
              <Route path="history" element={<HistoryModal />} />
              {/* Два адреса на одно окно: `/app/affiliate/overview` -
                  соответствие эталону (`affiliate.modal` реестра), короткий
                  остаётся для ссылок, которые уже разошлись по продукту. */}
              <Route path="affiliate" element={<AffiliateModal />} />
              <Route path="affiliate/overview" element={<AffiliateModal />} />
              <Route path="games" element={<GamesModal />} />
              <Route path="plan" element={<PlanModal />} />
              {/* Возврат с платёжной страницы: адреса, которые мы отдаём
                  провайдеру (`payments/returns.ts`). Существуют ДО его
                  подключения - при подключении они ломаются первыми. */}
              <Route path="billing/done" element={<BillingDoneModal />} />
              <Route path="billing/cancelled" element={<BillingCancelledModal />} />
              <Route path="feedback/:kind" element={<FeedbackRoute />} />
            </Route>

            {/* Прежние экраны зон до переезда в консоль: без вкладок. */}
            <Route
              path="/app/legacy"
              element={
                <RequireAuth>
                  <LegacyZoneLayout />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/app/legacy/overlay" replace />} />
              <Route path="overlay" element={<OverlayZone />} />
              <Route path="stream" element={<StreamZone />} />
              <Route path="channels" element={<ChannelsZone />} />
              <Route path="account" element={<AccountZone />} />

              {ZONES.filter(
                (zone) =>
                  zone.id !== 'overlay' &&
                  zone.id !== 'stream' &&
                  zone.id !== 'channels' &&
                  zone.id !== 'bindings',
              ).map((zone) => (
                <Route key={zone.id} path={zone.id} element={<EmptyZone zone={zone} />} />
              ))}
            </Route>

            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
        </ToastProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}
